import React from "react";
import { Alert, Badge, Button } from "reactstrap";
import { BiChevronDown, BiChevronUp, BiGroup, BiRecycle, BiRightArrowAlt } from "react-icons/bi";
import { BsArrowUpRight } from "react-icons/bs";
import parse from "react-html-parser";
import { getInputTypeLabel } from "../data";
import "./Results/resultsComponents.css";

// Accepts:
//   question           — question object (for header rendering)
//   questionOrder      — question order number (used for the dark badge, padded to 2 digits)
//   questionText       — question text string (may contain HTML; rendered via react-html-parser)
//   responseCount      — number of respondents for this question
//   totalRespondents   — total people who could have answered
//   insightEntry       — questionInsightsIndex item { overview, themes[], actions[], risks[] } — fallback only
//   insights           — full v2 insights doc { parts: { overview, keyThemes, toneSentiment, recommendedActions, risks } }
//   isExpanded         — whether the full insights detail sections are shown
//   isLoading          — whether the insights request is in flight
//   canGetFullInsights — whether to show the "Get Full Insights" button (no full insights yet)
//   onGetFullInsights  — handler for "Get Full Insights" / "Hide Full Insights" toggle
//   onRegenerate       — handler for "Regenerate Insights"
//   sentimentNode      — sentiment-bar React node rendered by parent

const getInputTypeEyebrow = (question) => {
  const label = getInputTypeLabel(question.question_type);
  if (label) {
    return `OPEN-ENDED · ${label.toUpperCase()}`;
  }
  return "OPEN-ENDED";
};

const getOverviewText = (insights, insightEntry) => {
  if (
    insights &&
    insights.parts &&
    insights.parts.overview &&
    insights.parts.overview.status === "DONE" &&
    insights.parts.overview.content
  ) {
    return insights.parts.overview.content;
  }
  if (insightEntry && insightEntry.overview) {
    return insightEntry.overview;
  }
  return null;
};

const padOrder = (order) => {
  const num = typeof order === "number" ? order : parseInt(order, 10);
  if (isNaN(num)) return String(order || "");
  if (num < 10) return `0${num}`;
  return String(num);
};

// Renders "High frequency" or "Low frequency" badge. The BE may send the
// countHint as either a number (raw count of mentions) or a string ("High
// frequency" / "Low frequency"). Map both forms to the canonical text label
// so the UI always shows the same wording.
const FrequencyBadge = ({ countHint }) => {
  if (countHint === undefined || countHint === null || countHint === "") {
    return null;
  }
  let isHigh;
  if (typeof countHint === "number") {
    isHigh = countHint >= 2;
  } else {
    isHigh = String(countHint).toLowerCase().indexOf("high") >= 0;
  }
  const className = isHigh ? "tiv2_freq_badge_high" : "tiv2_freq_badge_low";
  const text = isHigh ? "High frequency" : "Low frequency";
  return <Badge className={className}>{text}</Badge>;
};

const KeyThemesSection = ({ keyThemes }) => {
  if (!keyThemes || keyThemes.status !== "DONE") return null;
  const themes = keyThemes.themes && keyThemes.themes.length > 0 ? keyThemes.themes : [];
  return (
    <div className="tiv2_section">
      <div className="tiv2_section_header">
        <div className="tiv2_section_eyebrow">KEY THEMES</div>
        <div className="tiv2_section_count">{themes.length} surfaced</div>
      </div>
      {themes.length > 0 && themes.map((theme, i) => (
        <div key={i} className="tiv2_theme">
          <div className="tiv2_theme_header">
            <h6 className="tiv2_theme_label">{theme.label}</h6>
            <FrequencyBadge countHint={theme.countHint} />
          </div>
          {theme.details && theme.details.length > 0 && (
            <ul className="tiv2_bullet_list">
              {theme.details.map((d, j) => (
                <li key={j}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};

const RecommendedActionsSection = ({ recommendedActions }) => {
  if (!recommendedActions || recommendedActions.status !== "DONE") return null;
  const actions =
    recommendedActions.actions && recommendedActions.actions.length > 0
      ? recommendedActions.actions
      : [];
  if (actions.length === 0) return null;
  return (
    <div className="tiv2_section">
      <div className="tiv2_section_header">
        <div className="tiv2_section_eyebrow">RECOMMENDED ACTIONS</div>
      </div>
      {actions.map((action, i) => (
        <div key={i} className="tiv2_action_row">
          <div className="tiv2_action_icon">
            <BiRightArrowAlt size={20} />
          </div>
          <div className="tiv2_action_body">
            {action.label && (
              <p className="tiv2_action_label">{action.label}</p>
            )}
            {action.action && (
              <p className="tiv2_action_text">{action.action}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const RisksSection = ({ risks }) => {
  if (!risks || risks.status !== "DONE") return null;
  const items = risks.items && risks.items.length > 0 ? risks.items : [];
  if (items.length === 0) return null;
  return (
    <div className="tiv2_section">
      <div className="tiv2_section_header">
        <div className="tiv2_section_eyebrow">RISKS &amp; NUANCES</div>
      </div>
      <ul className="tiv2_bullet_list">
        {items.map((item, i) => (
          <li key={i}>{item.description}</li>
        ))}
      </ul>
    </div>
  );
};

const TriviaInsightsV2 = ({
  question,
  questionOrder,
  questionText,
  responseCount,
  totalRespondents,
  insightEntry,
  insights,
  isExpanded,
  isLoading,
  canGetFullInsights,
  canRegenerate,
  canGenerate,
  onGetFullInsights,
  onRegenerate,
  onGenerate,
  sentimentNode,
}) => {
  // Fallback when no question context is provided — preserves backwards-compat
  // for any caller that still passes only the legacy `insights`/`insightEntry` props.
  if (!question) {
    if (insights && insights.parts) {
      return (
        <div className="tiv2_card">
          {getOverviewText(insights, insightEntry) && (
            <p className="tiv2_overview">{getOverviewText(insights, insightEntry)}</p>
          )}
          <KeyThemesSection keyThemes={insights.parts.keyThemes} />
          <RecommendedActionsSection recommendedActions={insights.parts.recommendedActions} />
          <RisksSection risks={insights.parts.risks} />
        </div>
      );
    }
    if (!insightEntry) return null;
    return (
      <div className="tiv2_card">
        {insightEntry.overview && (
          <p className="tiv2_overview">{insightEntry.overview}</p>
        )}
      </div>
    );
  }

  const overviewText = getOverviewText(insights, insightEntry);
  const inputTypeEyebrow = getInputTypeEyebrow(question);
  const hasFullInsights = !!(insights && insights.parts);
  const showExpanded = isExpanded && hasFullInsights;

  // Primary button mode:
  //   - "toggle": v2 data already loaded → show Get/Hide Full Insights toggle
  //   - "fetch":  v2 data exists in questionInsightsIndex but not yet fetched → show Get Full Insights
  //   - "generate": nothing exists yet → show Generate Insights
  //   - null: no primary button
  let primaryButtonMode = null;
  if (hasFullInsights) {
    primaryButtonMode = "toggle";
  } else if (canGetFullInsights) {
    primaryButtonMode = "fetch";
  } else if (canGenerate) {
    primaryButtonMode = "generate";
  }

  let primaryButtonLabel = "";
  let primaryButtonHandler = onGetFullInsights;
  if (primaryButtonMode === "toggle") {
    primaryButtonLabel = showExpanded ? "Hide Full Insights" : "Get Full Insights";
  } else if (primaryButtonMode === "fetch") {
    primaryButtonLabel = "Get Full Insights";
  } else if (primaryButtonMode === "generate") {
    primaryButtonLabel = "Generate Insights";
    primaryButtonHandler = onGenerate;
  }

  const orderForBadge = typeof questionOrder !== "undefined" ? questionOrder : question.order;
  const titleSource = typeof questionText === "string" ? questionText : question.question_text;
  const respCount = typeof responseCount === "number" ? responseCount : 0;
  const totalResp = typeof totalRespondents === "number" ? totalRespondents : 0;
  const responseRate = totalResp > 0 ? Math.round((respCount / totalResp) * 100) : 0;

  return (
    <div className="tiv2_card">

      {/* Header — question-order chip (same style as parent's existing chip),
          stacked title block, Export button */}
      <div className="tiv2_header">
        <div
          className={`TriviaEditViewListUI_leftContainer_singleQuestion_chip_color_${
            (typeof orderForBadge === "number" ? orderForBadge : parseInt(orderForBadge, 10)) % 2 === 0
              ? "even"
              : "odd"
          } tiv2_order_chip`}
          style={
            question && question.theme_settings
              ? {
                  backgroundColor: question.theme_settings.quizBackgroundColor,
                  color: question.theme_settings.quizTextColor,
                }
              : {}
          }
        >
          <span>{orderForBadge}</span>
        </div>
        <div className="tiv2_header_text">
          <div className="tiv2_eyebrow">{inputTypeEyebrow}</div>
          <h4 className="tiv2_title">{parse(titleSource || "")}</h4>
          <div className="tiv2_respondents">
            <BiGroup size={16} />
            <span>
              {respCount} of {totalResp} respondents · {responseRate}% response rate
            </span>
          </div>
        </div>
        {/* <Button className="tiv2_export_btn" size="sm">
          Export <BsArrowUpRight size={13} />
        </Button> */}
      </div>

      {/* Sentiment bar with stacked labels (divider above via CSS) */}
      {sentimentNode && (
        <div className="tiv2_sentiment_wrapper">
          {sentimentNode}
        </div>
      )}

      {/* Overview */}
      {overviewText && (
        <p className="tiv2_overview">{overviewText}</p>
      )}

      {/* Actions row (divider above via CSS). Bottom divider is suppressed
          when the card is collapsed (button shows "Get Full Insights") since
          there's nothing below the buttons in that state. */}
      <div className={`tiv2_actions ${!showExpanded ? "tiv2_actions_collapsed" : ""}`}>
        {isLoading ? (
          <Alert className="tiv2_loading_alert">
            Insights are being generated...
          </Alert>
        ) : (
          <React.Fragment>
            {primaryButtonMode && (
              <Button
                className="tiv2_primary_btn"
                size="sm"
                onClick={primaryButtonHandler}
              >
                {primaryButtonLabel}
                {primaryButtonMode === "generate" ? (
                  <BiRecycle />
                ) : showExpanded ? (
                  <BiChevronUp size={18} />
                ) : (
                  <BiChevronDown size={18} />
                )}
              </Button>
            )}
            {canRegenerate && (
              <Button
                className="tiv2_secondary_btn"
                size="sm"
                onClick={onRegenerate}
              >
                Regenerate Insights
                <BiRecycle />
              </Button>
            )}
          </React.Fragment>
        )}
      </div>

      {/* Expanded detail sections */}
      {showExpanded && (
        <div className="tiv2_expanded">
          <KeyThemesSection keyThemes={insights.parts.keyThemes} />
          <RecommendedActionsSection recommendedActions={insights.parts.recommendedActions} />
          <RisksSection risks={insights.parts.risks} />
        </div>
      )}
    </div>
  );
};

export default TriviaInsightsV2;
